import { useScan } from './useScan';
import { GRADE_LABEL, SEVERITY_LABEL, buildResultView, letterTone } from '@/lib/vscanner/result-view';
import type { ScanResult } from '@/lib/vscanner/types';
import styles from './VScannerBanner.module.css';

interface ScanResultsProps {
  result: ScanResult;
  onLogin: () => void;
  onSignup: () => void;
}

export function ScanResults({ result, onLogin, onSignup }: ScanResultsProps) {
  const { reset } = useScan();
  const view = buildResultView(result);
  const { metrics } = result;
  const first = result.findings[0];
  
  return (
    <>
      <div className={styles.results} data-theme="light">
        {result.partial ? (
          <div className={styles.partial} role="status">
            <strong>Partial results.</strong> {result.partial}
          </div>
        ) : null}
        <div className={styles.resultsBody}>
          <div className={styles.bento}>
            <section className={`${styles.tile} ${styles.scoreTile}`} aria-label="Total findings">
              <div className={styles.tileLabel}>Total findings</div>
              <div className={styles.metricValue}>
                <span className={`${styles.metricBig} ${styles.metricStrong}`}>{view.total}</span>
                <span className={styles.metricUnit}>findings by severity</span>
              </div>
              <div className={styles.sevChart} role="img" aria-label="Findings by severity">
                {view.bars.map((bar) => (
                  <div key={bar.severity} className={styles.sevCol}>
                    <div className={styles.sevCount}>{bar.count}</div>
                    <div className={styles.sevTrack}>
                      <div
                        className={`${styles.sevFill} ${styles[`sev-${bar.severity}`]}`}
                        style={{ height: bar.height }}
                      />
                    </div>
                    <div className={styles.sevName}>{SEVERITY_LABEL[bar.severity]}</div>
                  </div>
                ))}
              </div>
              <div className={styles.verdict}>
                <strong>{GRADE_LABEL[result.grade]}:</strong> {view.verdict}
              </div>
            </section>

            <section className={`${styles.tile} ${styles.metricTile}`} aria-label="Security Headers">
              <div className={styles.tileLabel}>Security Headers</div>
              <div className={styles.metricValue}>
                <span className={`${styles.metricBig} ${styles[`tone-${letterTone(metrics.headers.grade)}`]}`}>
                  {metrics.headers.grade}
                </span>
                <span className={styles.metricUnit}>
                  {metrics.headers.score}/100 · {metrics.headers.present}/{metrics.headers.total} header
                </span>
              </div>
              <div className={styles.cells} role="img" aria-label={`${metrics.headers.present} of ${metrics.headers.total} headers`}>
                {metrics.headers.cells.map((c) => (
                  <span key={c.name} title={c.name} className={`${styles.cell} ${c.present ? styles.cellOn : styles.cellOff}`} />
                ))}
              </div>
              <div className={styles.metricNote}>Missing: {view.missingText}</div>
            </section>

            <section className={`${styles.tile} ${styles.metricTile}`} aria-label="SSL / TLS">
              <div className={styles.tileLabel}>SSL / TLS</div>
              <div className={styles.metricValue}>
                <span className={`${styles.metricBig} ${styles[`tone-${letterTone(metrics.tls.grade)}`]}`}>
                  {metrics.tls.grade}
                </span>
                <span className={styles.metricUnit}>{metrics.tls.version}</span>
              </div>
              <div className={styles.cells}>
                {metrics.tls.protocols.map((p) => (
                  <span
                    key={p.name}
                    className={`${styles.proto} ${!p.supported ? styles.protoOff : p.secure ? styles.protoOk : styles.protoWeak}`}
                  >
                    {p.name.replace('TLS ', '')}
                  </span>
                ))}
              </div>
              <div className={styles.metricNote}>
                {metrics.tls.issuer ? `${metrics.tls.issuer} · ` : ''}
                {metrics.tls.daysLeft} days left
              </div>
            </section>

            <section className={`${styles.tile} ${styles.metricTile}`} aria-label="OWASP Top 10">
              <div className={styles.owaspHead}>
                <span className={styles.tileLabel}>OWASP Top 10</span>
                <span className={styles.owaspHint}>findings / category</span>
              </div>
              <div className={`${styles.metricValue} ${styles.metricValueTight}`}>
                <span className={`${styles.metricBig} ${styles.metricStrong}`}>{metrics.owasp.issues}</span>
                <span className={styles.metricUnit}>
                  findings · {metrics.owasp.flagged}/{metrics.owasp.total} categories
                </span>
              </div>
              <div className={styles.owaspChart} role="img" aria-label="Findings per OWASP category, A01 to A10">
                <svg width="100%" height="100%" viewBox={view.owasp.viewBox} preserveAspectRatio="none" fill="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="vsOwaspFill" x1="0" y1="44" x2="0" y2="4" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#fff" />
                      <stop offset="1" stopColor="#fcd0d0" />
                    </linearGradient>
                  </defs>
                  <path d="M4 4H92M4 14H92M4 24H92M4 34H92M4 44H92" className={styles.owaspGrid} />
                  <path d={view.owasp.grid} className={styles.owaspGrid} />
                  <path d={view.owasp.area} fill="url(#vsOwaspFill)" />
                  <path d={view.owasp.line} className={styles.owaspLine} />
                </svg>
                <span className={styles.owaspDot} style={{ left: view.owasp.dot.left, top: view.owasp.dot.top }} />
                <span
                  className={`${styles.owaspTip} ${view.owasp.tipBelow ? styles.owaspTipBelow : ''}`}
                  style={{ left: view.owasp.dot.left, top: view.owasp.dot.top }}
                >
                  {view.owasp.peakLabel}
                </span>
              </div>
              <div className={styles.owaspAxis} aria-hidden="true">
                {view.owasp.axis.map((a) => (
                  <span key={a.label} className={a.active ? styles.owaspAxisOn : undefined}>
                    {a.label}
                  </span>
                ))}
              </div>
            </section>

            <section className={`${styles.tile} ${styles.metricTile}`} aria-label="SSL/TLS vulnerabilities">
              <div className={styles.tileLabel}>SSL/TLS vulnerabilities</div>
              <div className={`${styles.metricValue} ${styles.metricValueTight}`}>
                <span className={`${styles.metricBig} ${styles[`tone-${metrics.tls.vulns.length ? 'bad' : 'good'}`]}`}>
                  {metrics.tls.vulns.length}
                </span>
                <span className={styles.metricUnit}>{metrics.tls.vulns.length ? 'detected' : 'none detected'}</span>
              </div>
              <div className={styles.vulnList}>
                {metrics.tls.vulns.slice(0, 3).map((v) => (
                  <div key={v} className={styles.vuln}>{v}</div>
                ))}
              </div>
            </section>

            <section className={`${styles.tile} ${styles.issuesTile}`} aria-label="Issues to fix">
              <div className={styles.issuesHead}>
                <div className={styles.issuesTitle}>Issues to fix</div>
                <div className={styles.locked}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  {view.lockedCount} more findings after sign-in
                </div>
              </div>
              {first ? (
                <div className={styles.finding}>
                  <span className={`${styles.sev} ${styles[`sev-${first.severity}`]}`}>
                    {SEVERITY_LABEL[first.severity]}
                  </span>
                  <div className={styles.findingText}>
                    <div className={styles.findingTitle}>{first.title}</div>
                    <div className={styles.findingDetail}>{first.detail}</div>
                  </div>
                </div>
              ) : null}
              <div className={styles.lockedPlaceholder} aria-hidden="true">
                <div className={styles.placeholderRow}>
                  <span className={styles.placeholderPill} />
                  <div className={styles.placeholderLines}>
                    <div className={styles.placeholderLine1} />
                    <div className={styles.placeholderLine2} />
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className={styles.overlay}>
            <div className={styles.ctas}>
              <button type="button" className={styles.ctaPrimary} onClick={onLogin}>
                View full report
              </button>
              <button type="button" className={styles.ctaSecondary} onClick={onSignup}>
                Create an account
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <button type="button" className={styles.backLink} onClick={reset}>
        ← Scan another website
      </button>
    </>
  );
}
